import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SidebarSubmenu } from './sidebar-submenu';
import { SidebarMenuItemProps } from './types';

export function SidebarMenuItem({
  item,
  isActive,
  isCollapsed,
  currentPath,
  onNavigate,
  onToggleSubMenu,
  isSubMenuOpen = false,
}: SidebarMenuItemProps) {
  const { t } = useTranslation('layout');
  const IconComponent = item.icon;

  if (item.submenu) {
    return (
      <Collapsible open={isSubMenuOpen} onOpenChange={() => onToggleSubMenu?.(item.title)}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              'flex items-center w-full px-3 py-2 my-1 rounded-md transition-colors',
              isCollapsed ? 'justify-center' : 'justify-between',
              isActive
                ? 'bg-blue-700 text-white hover:bg-blue-700'
                : 'text-white hover:bg-blue-800',
            )}
          >
            <div className="flex items-center gap-3">
              <IconComponent size={20} className="text-white" />
              {!isCollapsed && <span className="text-white">{t(item.title)}</span>}
            </div>
            {!isCollapsed && (
              <div className="flex items-center gap-2">
                <ChevronRight
                  size={16}
                  className={cn(
                    'transition-transform text-white',
                    isSubMenuOpen && 'transform rotate-90',
                  )}
                />
                {item.badge && (
                  <Badge variant="destructive" className="ml-2">
                    {item.badge}
                  </Badge>
                )}
              </div>
            )}
            {item.badge && isCollapsed && (
              <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
                {item.badge}
              </span>
            )}
          </Button>
        </CollapsibleTrigger>
        {!isCollapsed && (
          <CollapsibleContent>
            <SidebarSubmenu
              items={item.submenu}
              currentPath={currentPath}
              onNavigate={onNavigate}
            />
          </CollapsibleContent>
        )}
      </Collapsible>
    );
  }

  return (
    <Button
      variant="ghost"
      onClick={() => onNavigate(item.path)}
      className={cn(
        'flex items-center w-full px-3 py-2 my-1 rounded-md transition-colors relative',
        isCollapsed ? 'justify-center' : 'justify-between',
        isActive ? 'bg-blue-700 text-white hover:bg-blue-700' : 'text-white hover:bg-blue-800',
      )}
    >
      <div className="flex items-center gap-3">
        <IconComponent size={20} className="text-white" />
        {!isCollapsed && <span className="text-white">{t(item.title)}</span>}
      </div>
      {!isCollapsed && item.badge && <Badge variant="destructive">{item.badge}</Badge>}
      {item.badge && isCollapsed && (
        <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
          {item.badge}
        </span>
      )}
    </Button>
  );
}
